import React, { useState, useEffect, useCallback, useRef } from "react";
import { TextField, Box, FormControlLabel, Switch, Select, MenuItem, FormControl, InputLabel, ListItemIcon, Button, IconButton, Tooltip } from "@material-ui/core";
import { SERVER_URL } from "../../../../utils";
import ImageIcon from "@material-ui/icons/Image";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import CancelIcon from "@material-ui/icons/Cancel";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import ToggleOnIcon from "@material-ui/icons/ToggleOn";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import ViewListIcon from "@material-ui/icons/ViewList";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import GridOnIcon from "@material-ui/icons/GridOn";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import ShortTextIcon from "@material-ui/icons/ShortText";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";

const genId = () => `mi_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const OPTIONS_LAYOUTS = [
	{ id: "row", label: "Fila", Icon: ViewListIcon, desc: "Vertical" },
	{ id: "columns", label: "Columnas", Icon: ViewColumnIcon, desc: "Horizontal" },
	{ id: "grid", label: "Cuadrícula", Icon: GridOnIcon, desc: "Grid" },
];

const QUESTION_TYPES = [
	{ id: "trueFalse", label: "Verdadero / Falso", Icon: ToggleOnIcon },
	{ id: "multipleChoice", label: "Opciones", Icon: FormatListBulletedIcon },
	{ id: "imageChoice", label: "Imágenes", Icon: ImageIcon },
	{ id: "optionsWithImages", label: "Opciones con imágenes", Icon: PhotoLibraryIcon },
	{ id: "match", label: "Emparejar (arrastrar)", Icon: SwapHorizIcon },
	{ id: "fillBlanks", label: "Completar huecos", Icon: FormatQuoteIcon },
	{ id: "textAnswer", label: "Respuesta escrita", Icon: ShortTextIcon },
];

function emptyQuestion(type = "multipleChoice") {
	const base = { optionsLayout: "row", optionsLayoutTablet: "", optionsLayoutMobile: "", gridColumns: 2 };
	if (type === "trueFalse") {
		return { ...base, type: "trueFalse", text: "", options: [{ text: "Verdadero", imageUrl: "", correct: true }, { text: "Falso", imageUrl: "", correct: false }], explanation: "" };
	}
	if (type === "match") {
		const l1 = { id: genId(), text: "Item 1", imageUrl: "" };
		const l2 = { id: genId(), text: "Item 2", imageUrl: "" };
		const r1 = { id: genId(), text: "Opción A", imageUrl: "" };
		const r2 = { id: genId(), text: "Opción B", imageUrl: "" };
		return { ...base, type: "match", text: "Empareja cada elemento", matchLeft: [l1, l2], matchRight: [r1, r2], matchPairs: [[l1.id, r1.id], [l2.id, r2.id]], options: [], explanation: "" };
	}
	if (type === "fillBlanks") {
		const defaultSegs = [{ type: "text", value: "La capital de Francia es ", segmentId: genId() }, { type: "blank", blankId: genId(), correctAnswer: "París" }, { type: "text", value: ".", segmentId: genId() }];
		return { ...base, type: "fillBlanks", text: "", segments: defaultSegs, fillOptions: ["París"], options: [], explanation: "" };
	}
	if (type === "textAnswer") {
		return { ...base, type: "textAnswer", text: "", correctAnswer: "", options: [], explanation: "" };
	}
	return { ...base, type: type || "multipleChoice", text: "", options: [{ text: "", imageUrl: "", correct: true }], explanation: "" };
}

function optionFromServer(o) {
	const base = { text: o?.text || "", imageUrl: o?.imageUrl || "", correct: !!o?.correct };
	const ap = o?.analyticsPoints;
	if (ap !== undefined && ap !== null && ap !== "" && Number.isFinite(Number(ap))) {
		base.analyticsPoints = Number(ap);
	}
	return base;
}

function normalizeQuestion(q) {
	const t = q?.type || "multipleChoice";
	const layout = q?.optionsLayout || "row";
	const layoutTablet = q?.optionsLayoutTablet != null ? String(q.optionsLayoutTablet) : "";
	const layoutMobile = q?.optionsLayoutMobile != null ? String(q.optionsLayoutMobile) : "";
	const gridCols = Math.min(6, Math.max(1, Number(q?.gridColumns) || 2));
	if (t === "trueFalse") {
		const o0 = (q?.options || [])[0] || {};
		const o1 = (q?.options || [])[1] || {};
		return {
			type: "trueFalse",
			text: q?.text || "",
			optionsLayout: layout,
			optionsLayoutTablet: layoutTablet,
			optionsLayoutMobile: layoutMobile,
			gridColumns: gridCols,
			options: [
				optionFromServer({
					...o0,
					text: (o0.text != null && String(o0.text).trim() !== "") ? String(o0.text) : "Verdadero",
				}),
				optionFromServer({
					...o1,
					text: (o1.text != null && String(o1.text).trim() !== "") ? String(o1.text) : "Falso",
				}),
			],
			explanation: q?.explanation || "",
		};
	}
	if (t === "match") {
		const left = (q?.matchLeft || []).map((o) => ({ id: o?.id || genId(), text: o?.text || "", imageUrl: o?.imageUrl || "" }));
		const right = (q?.matchRight || []).map((o) => ({ id: o?.id || genId(), text: o?.text || "", imageUrl: o?.imageUrl || "" }));
		const mpa = q?.matchPairAnalyticsPoints;
		const matchPairAnalyticsPoints =
			mpa && typeof mpa === "object" && !Array.isArray(mpa) ? { ...mpa } : {};
		return {
			type: "match",
			text: q?.text || "",
			optionsLayout: layout,
			optionsLayoutTablet: layoutTablet,
			optionsLayoutMobile: layoutMobile,
			gridColumns: gridCols,
			matchLeft: left.length ? left : [{ id: genId(), text: "", imageUrl: "" }],
			matchRight: right.length ? right : [{ id: genId(), text: "", imageUrl: "" }],
			matchPairs: q?.matchPairs || [],
			matchPairAnalyticsPoints,
			options: [],
			explanation: q?.explanation || "",
		};
	}
	if (t === "fillBlanks") {
		const segments = (q?.segments || []).map((s) => {
			if (s?.type === "blank") {
				const out = { type: "blank", blankId: s?.blankId || genId(), correctAnswer: s?.correctAnswer || "" };
				if (s?.analyticsPoints != null && Number.isFinite(Number(s.analyticsPoints))) {
					out.analyticsPoints = Number(s.analyticsPoints);
				}
				return out;
			}
			return { type: "text", value: s?.value || "", segmentId: s?.segmentId || genId() };
		});
		const fillOpts = [...new Set(segments.filter((s) => s.type === "blank" && (s.correctAnswer || "").trim()).map((s) => (s.correctAnswer || "").trim()))];
		return { type: "fillBlanks", text: q?.text || "", optionsLayout: layout, optionsLayoutTablet: layoutTablet, optionsLayoutMobile: layoutMobile, gridColumns: gridCols, segments: segments.length ? segments : [{ type: "text", value: "", segmentId: genId() }, { type: "blank", blankId: genId(), correctAnswer: "" }], fillOptions: fillOpts, options: [], explanation: q?.explanation || "" };
	}
	if (t === "textAnswer") {
		const rubric = Array.isArray(q?.textAnswerRubric)
			? q.textAnswerRubric.map((r) => ({
					id: r?.id || genId(),
					text: r?.text || "",
					...(r?.analyticsPoints != null && Number.isFinite(Number(r.analyticsPoints))
						? { analyticsPoints: Number(r.analyticsPoints) }
						: {}),
				}))
			: [];
		return {
			type: "textAnswer",
			text: q?.text || "",
			correctAnswer: q?.correctAnswer || "",
			textAnswerRubric: rubric,
			optionsLayout: layout,
			optionsLayoutTablet: layoutTablet,
			optionsLayoutMobile: layoutMobile,
			gridColumns: gridCols,
			options: [],
			explanation: q?.explanation || "",
		};
	}
	return {
		type: t,
		text: q?.text || "",
		optionsLayout: layout,
		optionsLayoutTablet: layoutTablet,
		optionsLayoutMobile: layoutMobile,
		gridColumns: gridCols,
		options: (q?.options || []).map((o) => optionFromServer(o)),
		explanation: q?.explanation || "",
	};
}

export default function InlineQuizEditor({ quiz, onSave, updateQuiz, userMedia = [], elementId, onRequestOptionImagePicker, quizInlineSyncNonce = 0, textAnswerCanvasSaveRef }) {
	const [question, setQuestion] = useState(() => normalizeQuestion(quiz?.questions?.[0]));
	const [pointsCorrect, setPointsCorrect] = useState(quiz?.pointsCorrect ?? 10);
	const [pointsIncorrect, setPointsIncorrect] = useState(quiz?.pointsIncorrect ?? -5);
	const [attemptMode, setAttemptMode] = useState(quiz?.attemptMode || (quiz?.allowMultipleAttempts ? "unlimited" : "single"));
	const [maxAttempts, setMaxAttempts] = useState(quiz?.maxAttempts ?? 3);
	const saveTimeoutRef = useRef(null);
	const questionRef = useRef(question);
	const pointsRef = useRef({ pointsCorrect, pointsIncorrect, attemptMode, maxAttempts });
	const isSyncingFromServerRef = useRef(false);
	const lastSyncedQuizIdRef = useRef(quiz?._id || null);
	const quizPropQuestionRef = useRef(quiz?.questions?.[0]);
	quizPropQuestionRef.current = quiz?.questions?.[0];
	const quizRefForSync = useRef(quiz);
	quizRefForSync.current = quiz;
	const updateQuizRef = useRef(updateQuiz);
	updateQuizRef.current = updateQuiz;
	const onSaveRef = useRef(onSave);
	onSaveRef.current = onSave;
	const skipNextQuestionPersistRef = useRef(false);
	const textAnswerPanelEditedRef = useRef(false);

	const getLiveQuizQuestion = useCallback(() => quizRefForSync.current?.questions?.[0] || quizPropQuestionRef.current, []);

	const getQuestionPayloadForPersist = useCallback((localQ) => {
		const live = getLiveQuizQuestion();
		if (localQ?.type === "fillBlanks") {
			if (live?.type === "fillBlanks") return normalizeQuestion(live);
			return localQ ? normalizeQuestion(localQ) : null;
		}
		if (localQ?.type === "match") return normalizeQuestion(localQ);
		if (
			live &&
			localQ?.type &&
			live.type === localQ.type &&
			["multipleChoice", "imageChoice", "trueFalse"].includes(localQ.type) &&
			Array.isArray(localQ.options) &&
			Array.isArray(live.options) &&
			live.options.length === localQ.options.length
		) {
			return normalizeQuestion({ ...localQ, options: live.options.map((o) => optionFromServer(o)) });
		}
		if (localQ?.type === "textAnswer" && live?.type === "textAnswer") {
			return normalizeQuestion({
				...localQ,
				correctAnswer: textAnswerPanelEditedRef.current
					? (localQ.correctAnswer ?? "")
					: (live.correctAnswer ?? localQ.correctAnswer ?? ""),
				textAnswerRubric: live.textAnswerRubric ?? localQ.textAnswerRubric,
			});
		}
		if (localQ?.type === "textAnswer") return normalizeQuestion(localQ);
		return localQ ? normalizeQuestion(localQ) : null;
	}, [getLiveQuizQuestion]);
	useEffect(() => {
		questionRef.current = question;
	}, [question]);
	useEffect(() => {
		pointsRef.current = { pointsCorrect, pointsIncorrect, attemptMode, maxAttempts };
	}, [pointsCorrect, pointsIncorrect, attemptMode, maxAttempts]);

	const quizId = quiz?._id;
	useEffect(() => {
		if (!quiz) return;
		const idChanged = String(quiz._id || "") !== String(lastSyncedQuizIdRef.current || "");
		if (idChanged) {
			isSyncingFromServerRef.current = true;
			setQuestion(normalizeQuestion(quiz.questions?.[0]));
			setPointsCorrect(quiz.pointsCorrect ?? 10);
			setPointsIncorrect(quiz.pointsIncorrect ?? -5);
			setAttemptMode(quiz.attemptMode || (quiz.allowMultipleAttempts ? "unlimited" : "single"));
			setMaxAttempts(quiz.maxAttempts ?? 3);
			lastSyncedQuizIdRef.current = quiz._id || null;
			isSyncingFromServerRef.current = false;
		}
	}, [quizId]);

	const persistQuiz = useCallback(async () => {
		if (!quiz?._id || !updateQuizRef.current) return;
		try {
			const localQ = questionRef.current;
			if (localQ?.type === "textAnswer" && textAnswerCanvasSaveRef?.current > 0) return;
			const q = getQuestionPayloadForPersist(localQ);
			if (!q) return;
			const { pointsCorrect: pc, pointsIncorrect: pi, attemptMode: am, maxAttempts: ma } = pointsRef.current;
			let qPayload = q;
			if (q.type === "fillBlanks") {
				const derivedFillOpts = [...new Set((q.segments || []).filter((s) => s.type === "blank" && (s.correctAnswer || "").trim()).map((s) => (s.correctAnswer || "").trim()))];
				qPayload = { ...q, fillOptions: derivedFillOpts };
			}
			const payload = {
				fullName: (q.text || "Pregunta").slice(0, 80),
				questions: [qPayload],
				pointsCorrect: Number(pc) || 0,
				pointsIncorrect: Number(pi) || 0,
				allowMultipleAttempts: am !== "single",
				attemptMode: am,
				maxAttempts: Math.max(1, Number(ma) || 3),
			};
			const res = await updateQuizRef.current(quiz._id, payload);
			if (onSaveRef.current) onSaveRef.current(res.data);
			if (localQ?.type === "textAnswer") textAnswerPanelEditedRef.current = false;
		} catch (e) {
			// Silently fail, user can retry
		}
	}, [quiz?._id, getQuestionPayloadForPersist, textAnswerCanvasSaveRef]);

	const debouncedSave = useCallback(() => {
		if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
		saveTimeoutRef.current = setTimeout(() => {
			persistQuiz();
			saveTimeoutRef.current = null;
		}, 600);
	}, [persistQuiz]);

	useEffect(() => {
		if (saveTimeoutRef.current) {
			clearTimeout(saveTimeoutRef.current);
			saveTimeoutRef.current = null;
		}
		const q = quizRefForSync.current;
		if (!q?._id) return;
		if (typeof document !== "undefined" && document.activeElement?.closest?.("[data-quiz-textanswer-panel], [data-quiz-textanswer-canvas]")) return;
		skipNextQuestionPersistRef.current = true;
		isSyncingFromServerRef.current = true;
		textAnswerPanelEditedRef.current = false;
		setQuestion(normalizeQuestion(q.questions?.[0]));
		setPointsCorrect(q.pointsCorrect ?? 10);
		setPointsIncorrect(q.pointsIncorrect ?? -5);
		setAttemptMode(q.attemptMode || (q.allowMultipleAttempts ? "unlimited" : "single"));
		setMaxAttempts(q.maxAttempts ?? 3);
		isSyncingFromServerRef.current = false;
	}, [quizInlineSyncNonce]);

	useEffect(() => {
		if (isSyncingFromServerRef.current) return;
		if (quiz?._id) debouncedSave();
	}, [pointsCorrect, pointsIncorrect, attemptMode, maxAttempts, debouncedSave, quiz?._id]);

	useEffect(() => {
		if (isSyncingFromServerRef.current) return;
		if (skipNextQuestionPersistRef.current) {
			skipNextQuestionPersistRef.current = false;
			return;
		}
		const t = questionRef.current?.type;
		if (!quiz?._id || t === "fillBlanks") return;
		debouncedSave();
	}, [question, debouncedSave, quiz?._id]);

	useEffect(() => {
		return () => {
			if (saveTimeoutRef.current) {
				clearTimeout(saveTimeoutRef.current);
				saveTimeoutRef.current = null;
			}
			persistQuiz();
		};
	}, [persistQuiz]);

	const setQ = (field, value) => setQuestion((q) => ({ ...q, [field]: value }));
	const toggleOptionCorrect = (i) => {
		setQuestion((q) => {
			const opts = [...(q.options || [])];
			if (q.type === "trueFalse") {
				return { ...q, options: opts.map((o, j) => ({ ...o, correct: j === i })) };
			}
			const toggled = !opts[i]?.correct;
			if (!toggled && opts.filter((o) => o.correct).length <= 1) return q;
			return { ...q, options: opts.map((o, j) => ({ ...o, correct: j === i ? toggled : o.correct })) };
		});
	};
	const removeImageOption = (i) => {
		setQuestion((q) => {
			const opts = [...(q.options || [])];
			if (opts.length <= 1) return q;
			const next = opts.filter((_, j) => j !== i);
			if (!next.some((o) => o.correct)) return { ...q, options: next.map((o, j) => ({ ...o, correct: j === 0 })) };
			return { ...q, options: next };
		});
	};

	const changeType = (newType) => {
		const prev = question;
		const q = emptyQuestion(newType);
		let next;
		if (newType === "trueFalse") next = q;
		else if (newType === "match" || newType === "fillBlanks" || newType === "textAnswer") next = { ...q, text: prev.text, explanation: prev.explanation };
		else {
			next = {
				...q,
				text: prev.text,
				explanation: prev.explanation,
				options: prev.options?.length ? prev.options.map((o) => ({ ...o, imageUrl: o.imageUrl || "" })) : q.options,
			};
		}
		setQuestion(next);
		questionRef.current = next;
		if (quiz?._id) persistQuiz();
	};

	if (!quiz) return null;

	return (
		<Box display="flex" flexDirection="column" style={{ gap: 24 }}>
			{/* Tipo */}
			<Box>
				<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>Tipo</Box>
				<FormControl variant="outlined" fullWidth size="medium">
					<InputLabel>Tipo de pregunta</InputLabel>
					<Select
						value={question.type || "multipleChoice"}
						onChange={(e) => changeType(e.target.value)}
						label="Tipo de pregunta"
						style={{ fontSize: 15 }}
					>
						{QUESTION_TYPES.map(({ id, label, Icon }) => (
							<MenuItem key={id} value={id}>
								<ListItemIcon><Icon fontSize="small" style={{ minWidth: 36 }} /></ListItemIcon>
								{label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			{/* Match: pares pregunta-respuesta (izquierda-derecha) */}
			{question.type === "match" && (
				<Box>
					<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>Emparejar elementos</Box>
					<Box display="flex" flexDirection="column" style={{ gap: 12 }}>
						{(() => {
							const leftArr = question.matchLeft || [];
							const rightArr = question.matchRight || [];
							const pairCount = Math.min(leftArr.length, rightArr.length);
							return Array.from({ length: pairCount }, (_, i) => {
								const left = leftArr[i];
								const right = rightArr[i];
								return (
									<Box key={left.id + "-" + right.id} display="flex" alignItems="center" p={1.5} borderRadius={10} style={{ gap: 12, background: "#f8fafc", border: "1px solid #e2e8f0", flexWrap: "wrap" }}>
										<Box flex={1} minWidth={120} display="flex" alignItems="center" style={{ gap: 8 }}>
											<TextField size="small" placeholder="Pregunta" value={left.text} onChange={(e) => setQ("matchLeft", (question.matchLeft || []).map((l, j) => j === i ? { ...l, text: e.target.value } : l))} style={{ flex: 1 }} />
											{onRequestOptionImagePicker && elementId && (
												<Button size="small" variant="outlined" startIcon={<ImageIcon />} onClick={() => onRequestOptionImagePicker(elementId, `matchLeft_${i}`)}>Img</Button>
											)}
										</Box>
										<SwapHorizIcon style={{ color: "#94a3b8", flexShrink: 0 }} />
										<Box flex={1} minWidth={120} display="flex" alignItems="center" style={{ gap: 8 }}>
											<TextField size="small" placeholder="Respuesta" value={right.text} onChange={(e) => setQ("matchRight", (question.matchRight || []).map((r, j) => j === i ? { ...r, text: e.target.value } : r))} style={{ flex: 1 }} />
											{onRequestOptionImagePicker && elementId && (
												<Button size="small" variant="outlined" startIcon={<ImageIcon />} onClick={() => onRequestOptionImagePicker(elementId, `matchRight_${i}`)}>Img</Button>
											)}
										</Box>
										<IconButton size="small" onClick={() => {
											const l = question.matchLeft || [];
											const r = question.matchRight || [];
											const n = Math.min(l.length, r.length);
											if (i >= n || n <= 1) return;
											const leftId = l[i].id;
											const rightId = r[i].id;
											setQuestion((q) => ({
												...q,
												matchLeft: (q.matchLeft || []).filter((_, j) => j !== i),
												matchRight: (q.matchRight || []).filter((_, j) => j !== i),
												matchPairs: (q.matchPairs || []).filter(([lid, rid]) => lid !== leftId && rid !== rightId),
											}));
										}} disabled={pairCount <= 1}><DeleteIcon fontSize="small" /></IconButton>
									</Box>
								);
							});
						})()}
						<Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => {
							const leftId = genId();
							const rightId = genId();
							setQuestion((q) => ({
								...q,
								matchLeft: [...(q.matchLeft || []), { id: leftId, text: "", imageUrl: "" }],
								matchRight: [...(q.matchRight || []), { id: rightId, text: "", imageUrl: "" }],
								matchPairs: [...(q.matchPairs || []), [leftId, rightId]],
							}));
						}}>Añadir pregunta</Button>
					</Box>
				</Box>
			)}

			{/* Fill blanks: se edita en el canvas */}

			{question.type === "textAnswer" && (
				<Box>
					<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>Respuesta correcta</Box>
					<TextField
						fullWidth
						size="medium"
						variant="outlined"
						placeholder="Texto que debe escribir el jugador (modo puntuado)"
						value={question.correctAnswer || ""}
						onChange={(e) => {
							textAnswerPanelEditedRef.current = true;
							setQ("correctAnswer", e.target.value);
						}}
						inputProps={{ "data-quiz-textanswer-panel": "1" }}
					/>
					<Box mt={1} fontSize={12} color="#64748b">
						En modo encuesta cualquier respuesta será válida.
					</Box>
				</Box>
			)}

			{/* Disposición - solo para tipos que la usan */}
			{(question.type === "multipleChoice" || question.type === "imageChoice" || question.type === "optionsWithImages" || question.type === "trueFalse") && (
			<Box>
				<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>Disposición</Box>
				<Box fontSize={12} color="#64748b" mb={1}>Ordenador (vista fija de escritorio en el lienzo).</Box>
				<Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
					<FormControl variant="outlined" size="medium" style={{ minWidth: 180, flex: 1 }}>
						<InputLabel>Ordenador</InputLabel>
						<Select
							value={question.optionsLayout || "row"}
							onChange={(e) => setQ("optionsLayout", e.target.value)}
							label="Ordenador"
							style={{ fontSize: 15 }}
						>
							{OPTIONS_LAYOUTS.map(({ id, label, Icon }) => (
								<MenuItem key={id} value={id}>
									<ListItemIcon><Icon fontSize="small" style={{ minWidth: 36 }} /></ListItemIcon>
									{label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{question.optionsLayout === "grid" && (
						<FormControl variant="outlined" size="medium" style={{ minWidth: 100 }}>
							<InputLabel>Columnas</InputLabel>
							<Select
								value={question.gridColumns || 2}
								onChange={(e) => setQ("gridColumns", Number(e.target.value))}
								label="Columnas"
								style={{ fontSize: 15 }}
							>
								{[2, 3, 4, 5, 6].map((n) => (
									<MenuItem key={n} value={n}>{n}</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
				</Box>
				<Box fontSize={12} color="#64748b" mb={0.5} mt={2}>Tableta y móvil (reproducción y pestañas de vista); vacío = heredar ordenador.</Box>
				<FormControl variant="outlined" size="medium" fullWidth style={{ marginTop: 8 }}>
					<InputLabel>Tableta</InputLabel>
					<Select
						value={question.optionsLayoutTablet || ""}
						onChange={(e) => setQ("optionsLayoutTablet", e.target.value)}
						label="Tableta"
						style={{ fontSize: 15 }}
					>
						<MenuItem value=""><em>Igual que ordenador</em></MenuItem>
						{OPTIONS_LAYOUTS.map(({ id, label, Icon }) => (
							<MenuItem key={id} value={id}>
								<ListItemIcon><Icon fontSize="small" style={{ minWidth: 36 }} /></ListItemIcon>
								{label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl variant="outlined" size="medium" fullWidth style={{ marginTop: 10 }}>
					<InputLabel>Móvil</InputLabel>
					<Select
						value={question.optionsLayoutMobile || ""}
						onChange={(e) => setQ("optionsLayoutMobile", e.target.value)}
						label="Móvil"
						style={{ fontSize: 15 }}
					>
						<MenuItem value=""><em>Igual que ordenador</em></MenuItem>
						{OPTIONS_LAYOUTS.map(({ id, label, Icon }) => (
							<MenuItem key={`m-${id}`} value={id}>
								<ListItemIcon><Icon fontSize="small" style={{ minWidth: 36 }} /></ListItemIcon>
								{label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>
			)}

			{/* Opciones con imágenes */}
			{(question.type === "imageChoice" || question.type === "optionsWithImages") && (
				<Box>
					<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>Imágenes de las opciones</Box>
					<Box display="flex" flexDirection="column" style={{ gap: 10 }}>
						{question.options?.map((opt, i) => (
							<Box
								key={i}
								display="flex"
								alignItems="center"
								p={1}
								borderRadius={10}
								style={{
									gap: 8,
									background: "#fff",
									border: "1px solid #e2e8f0",
									flexWrap: "nowrap",
									minWidth: 0,
								}}
							>
								<Box
									onDoubleClick={() => onRequestOptionImagePicker && elementId && onRequestOptionImagePicker(elementId, i)}
									style={{
										width: 56,
										height: 56,
										borderRadius: 8,
										overflow: "hidden",
										flexShrink: 0,
										background: "#f1f5f9",
										border: "2px dashed #cbd5e1",
										cursor: onRequestOptionImagePicker && elementId ? "pointer" : "default",
									}}
									title={onRequestOptionImagePicker && elementId ? "Doble clic para galería" : undefined}
								>
									{opt.imageUrl ? (
										<img src={opt.imageUrl.startsWith("http") || opt.imageUrl.startsWith("/") || opt.imageUrl.startsWith("data:") ? opt.imageUrl : `${SERVER_URL}/${opt.imageUrl}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} onError={(e) => { e.target.style.display = "none"; }} />
									) : (
										<Box display="flex" alignItems="center" justifyContent="center" height="100%" fontSize={9} color="#94a3b8" textAlign="center" px={0.5}>
											2× clic
										</Box>
									)}
								</Box>
								<Tooltip title={opt.correct ? "Correcta" : "Marcar correcta"}>
									<IconButton
										size="small"
										onClick={() => toggleOptionCorrect(i)}
										style={{
											padding: 4,
											flexShrink: 0,
											color: opt.correct ? "#16a34a" : "#94a3b8",
											background: opt.correct ? "rgba(34,197,94,0.12)" : "#f8fafc",
											border: `2px solid ${opt.correct ? "#22c55e" : "#e2e8f0"}`,
										}}
									>
										{opt.correct ? <CheckCircleIcon style={{ fontSize: 24 }} /> : <RadioButtonUncheckedIcon style={{ fontSize: 24 }} />}
									</IconButton>
								</Tooltip>
								{onRequestOptionImagePicker && elementId && (
									<Tooltip title="Elegir imagen">
										<IconButton
											size="small"
											onClick={() => onRequestOptionImagePicker(elementId, i)}
											style={{ flexShrink: 0, color: "#5b21b6", background: "#f5f3ff", border: "1px solid #ddd6fe" }}
										>
											<PhotoLibraryIcon style={{ fontSize: 22 }} />
										</IconButton>
									</Tooltip>
								)}
								{question.type === "imageChoice" && (
									<Box fontSize={12} fontWeight={600} color="#64748b" style={{ flexShrink: 0, minWidth: 56 }}>
										#{i + 1}
									</Box>
								)}
								{question.type === "optionsWithImages" && (
									<TextField
										size="small"
										value={opt.text}
										onChange={(e) => {
											const newOpts = [...(question.options || [])];
											newOpts[i] = { ...opt, text: e.target.value };
											setQ("options", newOpts);
										}}
										placeholder={`Texto opción ${i + 1}`}
										variant="outlined"
										style={{ flex: "1 1 0", minWidth: 72 }}
										InputProps={{ style: { fontSize: 13 } }}
									/>
								)}
								<Tooltip title="Eliminar opción">
									<span>
										<IconButton
											size="small"
											onClick={() => removeImageOption(i)}
											disabled={(question.options || []).length <= 1}
											style={{
												flexShrink: 0,
												color: "#b91c1c",
												background: "rgba(254,226,226,0.9)",
												border: "2px solid #fca5a5",
												padding: 6,
											}}
										>
											<DeleteIcon style={{ fontSize: 22 }} />
										</IconButton>
									</span>
								</Tooltip>
							</Box>
						))}
						<Button
							size="small"
							variant="outlined"
							startIcon={<AddIcon />}
							onClick={() =>
								setQuestion((q) => {
									const opts = [...(q.options || [])];
									const hasCorrect = opts.some((o) => o.correct);
									opts.push({ text: "", imageUrl: "", correct: !hasCorrect });
									return { ...q, options: opts };
								})
							}
							style={{ marginTop: 4, textTransform: "none", borderColor: "#c4b5fd", color: "#5b21b6" }}
						>
							Añadir opción
						</Button>
					</Box>
				</Box>
			)}

			{/* Puntos */}
			<Box>
				<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>Puntos</Box>
				<Box display="flex" style={{ gap: 12 }}>
					<Box
						flex={1}
						p={2}
						borderRadius={10}
						style={{
							background: "linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(34,197,94,0.04) 100%)",
							border: "1px solid rgba(34,197,94,0.3)",
						}}
					>
						<Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
							<CheckCircleIcon style={{ fontSize: 20, color: "#16a34a" }} />
							<Box fontSize={12} fontWeight={600} color="#16a34a">Acierto</Box>
						</Box>
						<TextField
							type="number"
							value={pointsCorrect}
							onChange={(e) => setPointsCorrect(Number(e.target.value) || 0)}
							variant="outlined"
							size="small"
							fullWidth
							inputProps={{ min: 0, style: { fontSize: 18, fontWeight: 600 } }}
							InputProps={{ style: { backgroundColor: "#fff" } }}
						/>
					</Box>
					<Box
						flex={1}
						p={2}
						borderRadius={10}
						style={{
							background: "linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(239,68,68,0.04) 100%)",
							border: "1px solid rgba(239,68,68,0.3)",
						}}
					>
						<Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
							<CancelIcon style={{ fontSize: 20, color: "#dc2626" }} />
							<Box fontSize={12} fontWeight={600} color="#dc2626">Fallo</Box>
						</Box>
						<TextField
							type="number"
							value={pointsIncorrect}
							onChange={(e) => setPointsIncorrect(Number(e.target.value) || 0)}
							variant="outlined"
							size="small"
							fullWidth
							inputProps={{ min: -100, style: { fontSize: 18, fontWeight: 600 } }}
							InputProps={{ style: { backgroundColor: "#fff" } }}
						/>
					</Box>
				</Box>
				<Box mt={2}>
					<Box fontSize={13} fontWeight={600} color="#334155" mb={1}>Intentos</Box>
					<FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 8 }}>
						<InputLabel>Modo de intentos</InputLabel>
						<Select
							value={attemptMode}
							onChange={(e) => setAttemptMode(e.target.value)}
							label="Modo de intentos"
						>
							<MenuItem value="single">Un solo intento</MenuItem>
							<MenuItem value="limited">Varios intentos (máx. N)</MenuItem>
							<MenuItem value="unlimited">Hasta acertar todas las correctas</MenuItem>
						</Select>
					</FormControl>
					{attemptMode === "limited" && (
						<TextField
							type="number"
							label="Máximo de intentos"
							value={maxAttempts}
							onChange={(e) => setMaxAttempts(Math.max(1, Number(e.target.value) || 1))}
							variant="outlined"
							size="small"
							fullWidth
							inputProps={{ min: 1, max: 20 }}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
}
